import { gql } from '@apollo/client'

export const AUTOSCHEDULER_ITEM_FIELDS = gql`
  fragment AutoschedulerItemFields on AutoschedulerItem {
    id
    accountId
    accountHandle
    type
    status
    text
    sourceTweetId
    sourceTweetUrl
    sourceTweetText
    sourceAuthorUsername
    sourceAuthorDisplayName
    sourceTweetCreatedAt
    sourceTweetMetrics {
      replies
      likes
      reposts
      views
    }
    sourceFamily
    sourceQuery
    productLine
    targetAudience
    angle
    relevanceScore
    viralityScore
    bestHour
    scoreBreakdown
    gateStatus
    gateReasons
    riskFlags
    agentSummary
    agentRecommendation
    agentNotes
    approvedAt
    scheduledFor
    sentAt
    postId
    failureReason
    staleAt
    archivedAt
    archiveReason
    createdAt
    updatedAt
  }
`

export const GET_AUTOSCHEDULER_ITEMS = gql`
  query GetAutoschedulerItems($status: AutoschedulerStatus, $type: AutoschedulerItemType, $limit: Int, $cursor: String) {
    getAutoschedulerItems(status: $status, type: $type, limit: $limit, cursor: $cursor) {
      items {
        ...AutoschedulerItemFields
      }
      nextCursor
      totalCount
      summary {
        readyForReview
        approved
        scheduled
        sent
        failed
        skipped
        archived
        rejectedByGate
        strongApproves
        risky
        staleSoon
      }
      accountStatus {
        accountId
        postingStatus
        reason
        cooldownUntil
        dailySent
        dailyBudget
        discoveryStatus
        discoveryStage
        discoveryEnabled
        discoveryIntervalMinutes
        discoverySourceQueries
        lastRunAt
        lastRunFinishedAt
        lastRunDurationMs
        nextRunAt
        lastQueriesScanned
        lastTweetsScanned
        lastCandidatesCreated
        lastDuplicatesSkipped
        lastRejectedByGate
        lastRunReviewLimited
        reviewLimit
        lastError
        updatedAt
      }
    }
  }
  ${AUTOSCHEDULER_ITEM_FIELDS}
`

export const GET_AUTOSCHEDULER_POLICY = gql`
  query GetAutoschedulerPolicy {
    getAutoschedulerPolicy {
      accountId
      minReplyScore
      minPostScore
      dailySendBudget
      maxRepliesPerRun
      minDelaySeconds
      maxDelaySeconds
      authorCooldownHours
      tweetMaxAgeHours
      requireSemanticDedup
      blockQuestionsInReplies
      blockUnverifiedStats
      blockFabricatedExperience
      bannedPhrases
      sourceFamilies
      activeHours
      giveRatio
      bridgeRatio
      askRatio
      createdAt
      updatedAt
    }
  }
`

export const SET_AUTOSCHEDULER_ITEM_PLATFORMS = gql`
  mutation SetAutoschedulerItemPlatforms($id: ID!, $platforms: [String!]!) {
    setAutoschedulerItemPlatforms(id: $id, platforms: $platforms) {
      id
      platforms
    }
  }
`

export const GET_AUTOSCHEDULER_REPLY_PROFILE = gql`
  query GetAutoschedulerReplyProfile {
    getAutoschedulerReplyProfile {
      accountId
      interviewCount
      lastInterviewAt
      handle
      accountType
      shortBio
      audience
      topicsToOwn
      topicsToAvoid
      competitorsToAvoid
      voiceRules
      styleRules
      verifiedFacts
      bannedClaims
      allowedStorySources
      requireSourceForFirstPersonClaims
      giveRatio
      bridgeRatio
      askRatio
      bannedExactPhrases
      bannedPatterns
      overusedAngles
      genericReplyPatterns
      minReplyScore
      minPostScore
      requireSemanticDedup
      requireSpecificityToSourceTweet
      blockQuestionsInReplies
      blockUnverifiedStats
      blockFabricatedExperience
      replyFreshnessMinutes
      approvalMode
      expiryBehavior
      isReadyForAutoScheduling
      missingRequiredFields
      createdAt
      updatedAt
    }
  }
`

export const GET_AUTOSCHEDULER_ONBOARDING_QUESTIONNAIRE = gql`
  query GetAutoschedulerOnboardingQuestionnaire {
    getAutoschedulerOnboardingQuestionnaire {
      requiredQuestions
      founderVoiceQuestions
      schedulerQuestions
    }
  }
`

export const UPDATE_AUTOSCHEDULER_REPLY_PROFILE = gql`
  mutation UpdateAutoschedulerReplyProfile($input: UpdateAutoschedulerReplyProfileInput!) {
    updateAutoschedulerReplyProfile(input: $input) {
      accountId
      handle
      accountType
      shortBio
      audience
      topicsToOwn
      topicsToAvoid
      competitorsToAvoid
      voiceRules
      styleRules
      verifiedFacts
      bannedClaims
      allowedStorySources
      requireSourceForFirstPersonClaims
      giveRatio
      bridgeRatio
      askRatio
      bannedExactPhrases
      bannedPatterns
      overusedAngles
      genericReplyPatterns
      minReplyScore
      minPostScore
      requireSemanticDedup
      requireSpecificityToSourceTweet
      blockQuestionsInReplies
      blockUnverifiedStats
      blockFabricatedExperience
      replyFreshnessMinutes
      approvalMode
      expiryBehavior
      isReadyForAutoScheduling
      missingRequiredFields
      createdAt
      updatedAt
    }
  }
`

export const GENERATE_AUTOSCHEDULER_ONBOARDING_STEP = gql`
  mutation GenerateAutoschedulerOnboardingStep($input: GenerateAutoschedulerOnboardingStepInput!) {
    generateAutoschedulerOnboardingStep(input: $input) {
      question
      helperText
      suggestedAnswers
      capturedFields
      remainingFields
      isComplete
      profile {
        accountId
        handle
        accountType
        shortBio
        audience
        topicsToOwn
        topicsToAvoid
        competitorsToAvoid
        voiceRules
        styleRules
        verifiedFacts
        bannedClaims
        allowedStorySources
        requireSourceForFirstPersonClaims
        giveRatio
        bridgeRatio
        askRatio
        bannedExactPhrases
        bannedPatterns
        overusedAngles
        genericReplyPatterns
        minReplyScore
        minPostScore
        requireSemanticDedup
        requireSpecificityToSourceTweet
        blockQuestionsInReplies
        blockUnverifiedStats
        blockFabricatedExperience
        replyFreshnessMinutes
        approvalMode
        expiryBehavior
        isReadyForAutoScheduling
        missingRequiredFields
        createdAt
        updatedAt
      }
    }
  }
`

export const AUTOSCHEDULER_POSTING_STATUS_FIELDS = gql`
  fragment AutoschedulerPostingStatusFields on AutoschedulerPostingStatus {
    accountId
    postingStatus
    reason
    cooldownUntil
    dailySent
    dailyBudget
    discoveryStatus
    discoveryEnabled
    discoveryIntervalMinutes
    discoverySourceQueries
    lastRunAt
    lastRunFinishedAt
    lastRunDurationMs
    nextRunAt
    lastQueriesScanned
    lastTweetsScanned
    lastCandidatesCreated
    lastDuplicatesSkipped
    lastRejectedByGate
    lastRunReviewLimited
    reviewLimit
    lastError
    updatedAt
  }
`

export const RUN_AUTOSCHEDULER_DISCOVERY_NOW = gql`
  mutation RunAutoschedulerDiscoveryNow {
    runAutoschedulerDiscoveryNow {
      status
      queriesScanned
      tweetsScanned
      candidatesCreated
      duplicatesSkipped
      rejectedByGate
      durationMs
      sourceQueries
      error
      accountStatus {
        ...AutoschedulerPostingStatusFields
      }
    }
  }
  ${AUTOSCHEDULER_POSTING_STATUS_FIELDS}
`

export const UPDATE_AUTOSCHEDULER_DISCOVERY_SETTINGS = gql`
  mutation UpdateAutoschedulerDiscoverySettings($input: UpdateAutoschedulerDiscoverySettingsInput!) {
    updateAutoschedulerDiscoverySettings(input: $input) {
      ...AutoschedulerPostingStatusFields
    }
  }
  ${AUTOSCHEDULER_POSTING_STATUS_FIELDS}
`

export const UPDATE_AUTOSCHEDULER_ITEM = gql`
  mutation UpdateAutoschedulerItem($id: ID!, $input: UpdateAutoschedulerItemInput!) {
    updateAutoschedulerItem(id: $id, input: $input) {
      ...AutoschedulerItemFields
    }
  }
  ${AUTOSCHEDULER_ITEM_FIELDS}
`

export const APPROVE_AUTOSCHEDULER_ITEM = gql`
  mutation ApproveAutoschedulerItem($id: ID!) {
    approveAutoschedulerItem(id: $id) {
      ...AutoschedulerItemFields
    }
  }
  ${AUTOSCHEDULER_ITEM_FIELDS}
`

/** Approve into a time the user picked (a calendar drop), or move an approved item. */
export const SCHEDULE_AUTOSCHEDULER_ITEM = gql`
  mutation ScheduleAutoschedulerItem($id: ID!, $at: DateTimeISO!) {
    scheduleAutoschedulerItem(id: $id, at: $at) {
      ...AutoschedulerItemFields
    }
  }
  ${AUTOSCHEDULER_ITEM_FIELDS}
`

/** Post now: the only way a radar item goes out at once. Approve queues it. */
export const POST_AUTOSCHEDULER_ITEM_NOW = gql`
  mutation PostAutoschedulerItemNow($id: ID!) {
    postAutoschedulerItemNow(id: $id) {
      ...AutoschedulerItemFields
    }
  }
  ${AUTOSCHEDULER_ITEM_FIELDS}
`

export const SKIP_AUTOSCHEDULER_ITEM = gql`
  mutation SkipAutoschedulerItem($id: ID!, $reason: String) {
    skipAutoschedulerItem(id: $id, reason: $reason) {
      ...AutoschedulerItemFields
    }
  }
  ${AUTOSCHEDULER_ITEM_FIELDS}
`

export const ARCHIVE_AUTOSCHEDULER_ITEM = gql`
  mutation ArchiveAutoschedulerItem($id: ID!, $reason: String) {
    archiveAutoschedulerItem(id: $id, reason: $reason) {
      ...AutoschedulerItemFields
    }
  }
  ${AUTOSCHEDULER_ITEM_FIELDS}
`
